import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { IMAGES } from "../data/vehicles";
import {
  fadeInUp,
  stagger,
  HERO_EYEBROW,
  HERO_TITLE,
  HERO_SUB_LATE,
  REVEAL_FROM_LEFT,
} from "../lib/animations";

const HeroSection = () => (
  <section className="relative h-[60vh] min-h-[500px] overflow-hidden" data-testid="about-hero">
    <div className="absolute inset-0">
      <img 
        src={IMAGES.atmospheric[0]}
        alt="Mountain road"
        className="w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/50" />
    </div>

    <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-8 pt-24">
      <div className="max-w-[1400px] mx-auto w-full">
        <motion.p 
          className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-[#FF2A00] mb-4"
          {...HERO_EYEBROW}
        >
          The Story
        </motion.p>
        <motion.h1 
          className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-6"
          {...HERO_TITLE}
        >
          BACK TO ROOTS
        </motion.h1>
        <motion.p 
          className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl"
          {...HERO_SUB_LATE}
        >
          A story of repatriation, mechanical purity, and the analog revolution.
        </motion.p>
      </div>
    </div>
  </section>
);

const FounderStory = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8" data-testid="founder-story">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="max-w-4xl"
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            The Founder
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl tracking-tight mb-8">
            TAYLOR KELLER
          </motion.h2>
          
          <motion.div variants={fadeInUp} className="space-y-6 text-[#A3A3A3] leading-relaxed">
            <p>
              This idea started about 10 years ago, inspired by my time as a racing mechanic in the 
              California motorcycle racing league. I watched LMP racing cars and witnessed the foolish 
              phasing out of manual transmissions. Something had to be done.
            </p>
            
            <p>
              At Menlo College, I met Aidan DeCadenet, who introduced me to the Porsche 962. 
              That car became the foundation for what would eventually become the Seraph — our flagship hypercar. 
              The lines, the presence, the pure mechanical honesty of Group C racing would define everything we build.
            </p>
            
            <p>
              I then became the service manager at SZR Pro in Houston, where I discovered the VQ35DE motor. 
              Working with Jim Wolf Technology, we co-developed it to produce 800 horsepower — the heart 
              that now beats in every Keller Motors performance vehicle.
            </p>
            
            <p className="text-white text-lg">
              Then came the move that changed everything.
            </p>
            
            <p>
              I relocated from Houston to <span className="text-[#FF2A00]">Vaihingen an der Enz</span> in Germany — 
              repatriating to the exact village my ancestors lived in for 600 years. Enzweihingen isn't just 
              a location. It's a homecoming.
            </p>
            
            <p>
              This village sits just a few miles from the Porsche Weissach facility, near Stuttgart — 
              where the original internal combustion engine was developed. This is a back-to-roots story 
              about repatriation and the analog revolution, about getting back to the essence of what 
              a mechanical vehicle should be.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const LocationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8 bg-[#0F0F0F]" data-testid="location-section">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-12 md:gap-24 items-center">
        <motion.div
          initial={REVEAL_FROM_LEFT.initial}
          animate={isInView ? REVEAL_FROM_LEFT.target : {}}
          transition={REVEAL_FROM_LEFT.transition}
          className="relative"
        >
          <div className="aspect-square bg-[#050505] border border-[#262626] flex items-center justify-center">
            <div className="text-center p-8">
              <MapPin className="w-12 h-12 text-[#FF2A00] mx-auto mb-4" />
              <p className="font-display text-3xl mb-2">VAIHINGEN AN DER ENZ</p>
              <p className="text-[#A3A3A3]">Baden-Württemberg, Germany</p>
              <div className="mt-6 pt-6 border-t border-[#262626] text-sm text-[#A3A3A3]">
                <p>Near Porsche Weissach</p>
                <p>Near Stuttgart</p>
                <p>Birthplace of the ICE</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            Home Base
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl tracking-tight mb-8">
            WHERE ENGINES BEGAN
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-[#A3A3A3] leading-relaxed mb-6">
            We are currently operating out of Vaihingen an der Enz as we look to secure a permanent facility. 
            This location isn't a coincidence — it's a deliberate return to where the internal combustion 
            revolution began.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-[#A3A3A3] leading-relaxed">
            Stuttgart is where Karl Benz and Gottlieb Daimler developed the first automobiles. 
            Weissach is where Porsche continues to develop some of the world's finest driving machines. 
            And Enzweihingen is where my family has called home for 600 years. This is where we belong.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

const MissionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8" data-testid="mission-section">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            The Mission
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-6xl tracking-tight mb-8">
            THRILLING. AFFORDABLE.<br />BUILT TO LAST 100 YEARS.
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#A3A3A3] leading-relaxed mb-8">
            The objective of Keller Motors is to produce radically exciting and affordable sports cars, 
            while sparking a movement that causes every driver to rethink what they actually need from a car — 
            transportation and a bit of inspiration. Nothing more. Nothing less.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-lg text-white leading-relaxed">
            Our vision is to create a world where cars are thrilling, affordable, approachable, 
            and last 100 years.
          </motion.p>
        </motion.div>

        {/* Values grid */}
        <motion.div 
          className="grid md:grid-cols-3 gap-6 mt-16"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.div variants={fadeInUp} className="border border-[#262626] p-8">
            <h3 className="font-display text-xl mb-4">NO PROFIT FROM PAIN</h3>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              We pledge to never make profit from parts or service. When our customers are inconvenienced, 
              we should not benefit. Period.
            </p>
          </motion.div>
          <motion.div variants={fadeInUp} className="border border-[#262626] p-8">
            <h3 className="font-display text-xl mb-4">INDEFINITE PRODUCTION</h3>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              All of our cars are intended to be kept in production indefinitely. Nothing will ever 
              fundamentally change — except playful new designs and minor revisions if problems arise.
            </p>
          </motion.div>
          <motion.div variants={fadeInUp} className="border border-[#262626] p-8">
            <h3 className="font-display text-xl mb-4">BUILD YOUR ENGINE</h3>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              Every customer participates in their engine installation alongside our certified technicians. 
              This isn't just delivery — it's a rite of passage.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32 px-6 md:px-8 bg-[#0F0F0F]" data-testid="about-cta">
      <div className="max-w-[1400px] mx-auto text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-6xl tracking-tight mb-8">
            JOIN THE REVOLUTION
          </motion.h2>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/vehicles">
              <button className="font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white px-10 py-5 hover:bg-white hover:text-black transition-colors duration-200">
                View Vehicles
              </button>
            </Link>
            <Link to="/contact">
              <button className="font-mono text-sm uppercase tracking-wider border border-[#262626] text-white px-10 py-5 hover:bg-[#262626] transition-colors duration-200">
                Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-[#050505]">
      <Header />
      <HeroSection />
      <FounderStory />
      <div className="section-divider max-w-[1400px] mx-auto" />
      <LocationSection />
      <MissionSection />
      <CTASection />
      <Footer />
    </main>
  );
}
